import {CM_LOGGER_APP} from "@app/common/services/logging"
import {HttpClient} from "@angular/common/http"
import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    computed,
    inject,
    Injector,
    OnInit,
    viewChild,
    signal,
    DestroyRef,
    ChangeDetectionStrategy,
    effect,
    untracked,
} from "@angular/core"
import {FormsModule} from "@angular/forms"
import {MatButtonModule} from "@angular/material/button"
import {MatIconModule} from "@angular/material/icon"
import {MatListModule} from "@angular/material/list"
import {MatTreeNestedDataSource} from "@angular/material/tree"
import {CdkTreeModule, NestedTreeControl} from "@angular/cdk/tree"
import {ActivatedRoute, Router} from "@angular/router"
import {MutationCreatePriceGraphInput, MutationUpdatePriceGraphInput, PriceGraphState} from "@generated"
import {Catalog, CatalogItem} from "@cm/pricing/catalogs/catalog-interface"
import {PricingNode} from "@cm/pricing/pricing-node"
import {ConditionalAmountGroupNode} from "@cm/pricing/nodes/conditional-amount-group-node"
import {ConditionalAmountNode} from "@cm/pricing/nodes/conditional-amount-node"
import {Currency} from "@cm/pricing/types/currency"
import {ConfiguratorVariant, PriceMap, PricedItem} from "@cm/pricing/types/pricing-types"
import {PricingContext} from "@cm/pricing/pricing-context"
import {PriceContainer} from "@cm/pricing/nodes/price-container"
import {PricedItemNode} from "@cm/pricing/nodes/priced-item-node"
import {checkConfigurationGroupNodes} from "@cm/pricing/verification"
import {fetchThrowingErrors} from "@common/helpers/api/fetch"
import {mutateThrowingErrors} from "@common/helpers/api/mutate"
import {DialogSize} from "@common/models/dialogs"
import {RoutedDialogComponent} from "@common/components/dialogs/routed-dialog/routed-dialog.component"
import {
    PriceMappingCreatePriceGraphGQL,
    PriceMappingDeletePriceGraphGQL,
    PriceMappingGetPriceGraphsGQL,
    PriceMappingOrganizationIdFromTemplateIdGQL,
    PriceMappingUpdatePriceGraphGQL,
} from "@pricing/components/price-mapping/price-mapping.generated"
import {downloadFromMemory} from "@app/common/helpers/utils/download-from-memory"
import {ExporterYed} from "@app/platform/helpers/simple-graph/exporter/exporter-yed"
import {NodeSettingsComponent} from "@app/pricing/components/node-settings/node-settings/node-settings.component"
import {PriceTableComponent} from "@app/pricing/components/price-table/price-table.component"
import {PriceNodeVisualizerComponent} from "@pricing/components/node-visualizers/price-nodes-visualizer/price-node-visualizer.component"
import {getPriceMap} from "@pricing/helpers"
import {PriceGraphAccessor, PriceGraphBuilder} from "@pricing/models/pricing/price-graph"
import {ConfiguratorComponent} from "@app/common/components/viewers/configurator/configurator/configurator.component"
import {ConfigMenuComponent} from "@app/common/components/viewers/configurator/config-menu/config-menu.component"
import {getCatalogFromFileInput} from "@app/pricing/helpers/catalog"
import {importCatalog, updatePrices} from "@app/pricing/helpers/catalog-update"
import {ConfigMenuService} from "@app/common/components/viewers/configurator/config-menu/services/config-menu.service"
import {PriceGraphData} from "@cm/pricing/catalogs/graph-helpers"
import {PricingService} from "@pricing/services/pricing.service"
import {takeUntilDestroyed} from "@angular/core/rxjs-interop"
import {lastValueFrom} from "rxjs"
import {findTemplateEntry, groupByCustomer, TEMPLATE_REGISTRY, TemplateEntry} from "@pricing/components/price-mapping/graph-registry"

type GraphSource = "prod" | "code"
type PriceSource = "db" | "catalog"
type ProdPriceGraph = {id: string; state: PriceGraphState; graph: Parameters<PriceGraphBuilder["initFromSerialized"]>[0]}
type RebuildInput = {
    entry: TemplateEntry | undefined
    graphSource: GraphSource
    priceSource: PriceSource
    catalogOverride: {name: string; catalog: Catalog} | undefined
    prodGraph: ProdPriceGraph | undefined
    configuratorReady: boolean
}

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-price-mapping",
    templateUrl: "./price-mapping.component.html",
    styleUrls: ["./price-mapping.component.scss"],
    imports: [
        RoutedDialogComponent,
        MatButtonModule,
        MatListModule,
        PriceTableComponent,
        CdkTreeModule,
        PriceNodeVisualizerComponent,
        FormsModule,
        ConfigMenuComponent,
        MatIconModule,
        NodeSettingsComponent,
        ConfiguratorComponent,
    ],
})
export class PriceMappingComponent implements OnInit, AfterViewInit {
    private readonly logger = inject(CM_LOGGER_APP)
    readonly $configuratorMenu = viewChild.required<ConfigMenuComponent>("configuratorMenu")
    readonly $configurator = viewChild.required<ConfiguratorComponent>("configurator")

    dialogSizes = DialogSize
    readonly $modified = signal(false)

    readonly $templateId = signal<string | undefined>(undefined)

    //Inputs for rebuild(). Handlers only set these, the graph and price map are rebuilt in one place.
    readonly entryGroups = groupByCustomer(TEMPLATE_REGISTRY)
    readonly $selectedEntry = signal<TemplateEntry | undefined>(undefined)
    readonly $graphSource = signal<GraphSource>("code")
    readonly $priceSource = signal<PriceSource>("catalog")
    readonly $catalogOverride = signal<{name: string; catalog: Catalog} | undefined>(undefined)
    readonly $prodGraph = signal<ProdPriceGraph | undefined>(undefined)
    readonly $configuratorReady = signal(false)
    readonly $activeCatalogLabel = computed(() => {
        const override = this.$catalogOverride()
        if (override) return `file: ${override.name}`
        return this.$selectedEntry()?.catalog.id ?? "none"
    })
    private rebuildRun = 0
    private readonly catalogCache = new Map<string, Promise<Catalog>>()
    private readonly organizationIdCache = new Map<string, Promise<string>>()
    priceGraphId?: string
    readonly $priceGraphStates = signal(Object.values(PriceGraphState))
    priceGraphState: PriceGraphState = PriceGraphState.Draft

    priceGraphBuilder = new PriceGraphBuilder("test")

    catalog?: Catalog
    catalogNew?: Catalog
    readonly $priceMap = signal<PriceMap | undefined>(undefined)
    readonly $availableCurrencies = computed<Currency[]>(() => {
        const priceMap = this.$priceMap()
        return priceMap ? PricingContext.getAvailableCurrencies(priceMap) : []
    })
    readonly $pricingContext = computed<PricingContext | undefined>(() => {
        const priceMap = this.$priceMap()
        const currency = this.$selectedCurrency() ?? this.$availableCurrencies()[0]
        if (!priceMap || currency === undefined) return undefined
        return new PricingContext(priceMap, currency)
    })

    readonly $currentPrices = signal<PricedItem[]>([])
    readonly $priceIncludesVat = computed<boolean>(() => this.$pricingContext()?.getPriceIncludesVat() ?? true)
    readonly $selectedCurrency = signal<Currency | undefined>(undefined)
    error: string = ""

    //move this to own component
    readonly $searchResults = signal<CatalogItem[]>([])
    readonly $selectedSearchItem = signal<CatalogItem | undefined>(undefined)

    childrenAccessor = (node: PricingNode): PricingNode[] => {
        if (node instanceof PricedItemNode || node instanceof PriceContainer) return node.getSubprices()
        if (node instanceof ConditionalAmountGroupNode) return node.getAmountNodes()
        if (node instanceof ConditionalAmountNode) return []

        throw new Error("Unexpected node type")
    }

    readonly treeControl = new NestedTreeControl<PricingNode>(this.childrenAccessor)

    readonly $dataSource = signal(new MatTreeNestedDataSource<PricingNode>())
    readonly $selectedTreeNode = signal<PricingNode | undefined>(undefined)

    readonly organizationIdFromTemplateIdGql = inject(PriceMappingOrganizationIdFromTemplateIdGQL)
    readonly priceGraphsGql = inject(PriceMappingGetPriceGraphsGQL)
    readonly deletePriceGraphGql = inject(PriceMappingDeletePriceGraphGQL)
    readonly createPriceGraphGql = inject(PriceMappingCreatePriceGraphGQL)
    readonly updatePriceGraphGql = inject(PriceMappingUpdatePriceGraphGQL)

    readonly injector = inject(Injector)
    readonly pricingService = inject(PricingService)
    private readonly router = inject(Router)
    private readonly route = inject(ActivatedRoute)
    private readonly http = inject(HttpClient)
    private readonly configMenuService = inject(ConfigMenuService)
    private readonly destroyRef = inject(DestroyRef)
    private readonly changeDetectorRef = inject(ChangeDetectorRef)
    hasChild = (_: number, node: PricingNode) => {
        if (node instanceof PricedItemNode || node instanceof PriceContainer) return node.getSubprices().length > 0
        if (node instanceof ConditionalAmountGroupNode) return node.getAmountNodes().length > 0
        if (node instanceof ConditionalAmountNode) return false
        throw new Error("Unexpected node type")
    }

    constructor() {
        effect(() => {
            const graphSource = this.$graphSource()
            const input: RebuildInput = {
                entry: this.$selectedEntry(),
                graphSource,
                priceSource: this.$priceSource(),
                catalogOverride: this.$catalogOverride(),
                //only a dependency in prod mode, so that saving a code graph (which updates $prodGraph) does not trigger a rebuild
                prodGraph: graphSource === "prod" ? this.$prodGraph() : undefined,
                configuratorReady: this.$configuratorReady(),
            }
            untracked(() => void this.rebuild(input))
        })
    }

    ngOnInit() {
        this.route.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => this.setTemplateId(params["templateuuid"]))
    }

    ngAfterViewInit() {
        if (this.$templateId()) this.load()
    }

    setTemplateId(templateId: string | undefined): void {
        this.$templateId.set(templateId)
        this.selectEntry(findTemplateEntry(templateId))
    }

    //From the dropdown: also switches the template, so entry and template cannot disagree.
    onEntrySelected(entry: TemplateEntry | undefined): void {
        this.selectEntry(entry)
        const templateId = entry?.templateIds[0]
        if (templateId && templateId !== this.$templateId()) {
            this.$templateId.set(templateId)
            this.load()
        }
    }

    private selectEntry(entry: TemplateEntry | undefined): void {
        if (entry === this.$selectedEntry()) return
        this.$selectedEntry.set(entry)
        this.$catalogOverride.set(undefined)
    }

    searchCatalog(searchTerm: string): void {
        if (this.catalog) {
            this.$searchResults.set(this.catalog.search(searchTerm))
        }
    }

    downloadGraph(): void {
        const simpleGraph = this.priceGraphBuilder.transformToSimpleGraph()
        const exporter = new ExporterYed()
        const xml = exporter.export(simpleGraph)

        downloadFromMemory(xml, "Pricegraph.graphml")
    }

    onSearchItemClick(catalogItem: CatalogItem): void {
        this.$selectedSearchItem.set(catalogItem)
    }

    onSearchItemDoubleClick(catalogItem: CatalogItem): void {
        this.$selectedSearchItem.set(catalogItem)

        const priceGraph = catalogItem.createPriceGraph((uniqueId: string) => {
            return this.priceGraphBuilder.getSubgraph(uniqueId)
        })

        this.priceGraphBuilder.addSubPriceGraph(priceGraph)
        this.$dataSource().data = this.priceGraphBuilder.getNodesForDisplay()
    }

    onTreeNodeClick(node: PricingNode, event: MouseEvent) {
        this.$selectedTreeNode.set(node)
        event.stopPropagation()
    }

    private canDrop(node: PricingNode): boolean {
        if (node instanceof PricedItemNode && node.parameters.condition) {
            return true
        }
        if (node instanceof ConditionalAmountNode) {
            return true
        }
        return node instanceof PriceContainer && !!node.parameters.condition
    }

    onDragOver(event: DragEvent, node: PricingNode): void {
        event.stopPropagation()
        if (!this.canDrop(node)) return
        event.preventDefault()
    }

    onDrop(event: DragEvent, node: PricingNode): void {
        event.preventDefault()
        event.stopPropagation()
        if (event.dataTransfer === null) return
        const serializedData = event.dataTransfer.getData("text/plain")
        const data = JSON.parse(serializedData) as ConfiguratorVariant

        if (!(node instanceof PricedItemNode) && !(node instanceof ConditionalAmountNode) && !(node instanceof PriceContainer))
            throw new Error("Unexpected node type")
        if (!node.canAddDependency(data.groupId, data.variantId)) throw new Error("Cannot add dependency") //the drag/drop api does not allow access to this data before the drop event

        this.priceGraphBuilder.addDependency(node, data).then(() => {
            this.updateCurrentConfigurations()
        })
    }

    load(): void {
        const templateId = this.$templateId()
        if (!templateId) throw new Error("No template uuid provided")
        this.$configuratorReady.set(false)
        this.$prodGraph.set(undefined)
        this.priceGraphId = undefined
        this.$configurator().loadConfiguratorData({templateId: templateId}, undefined) //price graph loading is triggered by loadingCompleted of the scene viewer to prevent errors.
    }

    async updateCatalog() {
        const templateId = this.$templateId()
        if (!this.catalog || !this.catalogNew || !templateId) throw new Error("Catalogs or templateId not initialized")
        const organizationId = (await fetchThrowingErrors(this.organizationIdFromTemplateIdGql)({templateId})).template.organizationId
        updatePrices(organizationId, this.catalog, this.catalogNew, this.injector)
    }

    async importCatalog() {
        const templateId = this.$templateId()
        if (!this.catalog || !templateId) throw new Error("Catalogs or templateId not initialized")
        const organizationId = (await fetchThrowingErrors(this.organizationIdFromTemplateIdGql)({templateId})).template.organizationId
        importCatalog(organizationId, this.catalog, this.injector)
    }

    //Triggered by loadingCompleted of the configurator. Only fetches the prod graph, building happens in rebuild().
    async loadPriceGraph() {
        const {priceGraphs} = await fetchThrowingErrors(this.priceGraphsGql)({filter: {templateId: {equals: this.$templateId()}}})
        if (priceGraphs.length > 1) throw new Error("More than one price graph found, revisions not implemented yet")
        const priceGraph = priceGraphs[0]

        //Always keep the prod id, so that saving a code graph updates the existing graph instead of creating a second one.
        this.priceGraphId = priceGraph?.id
        this.priceGraphState = priceGraph?.state ?? PriceGraphState.Draft
        this.logger.debug("price graph id", this.priceGraphId)

        this.$prodGraph.set(priceGraph ? {id: priceGraph.id, state: priceGraph.state, graph: priceGraph.graph} : undefined)
        //Code graph and catalog prices by default; without a registry entry (or loaded catalog), fall back to prod.
        this.$graphSource.set(this.$selectedEntry() ? "code" : "prod")
        this.$priceSource.set(this.$selectedEntry() || this.$catalogOverride() ? "catalog" : "db")
        this.$configuratorReady.set(true)
    }

    private async rebuild(input: RebuildInput): Promise<void> {
        const run = ++this.rebuildRun
        if (!input.configuratorReady) return
        const isStale = () => run !== this.rebuildRun

        try {
            const catalog = input.catalogOverride?.catalog ?? (input.entry ? await this.getEntryCatalog(input.entry) : undefined)
            if (isStale()) return

            const builder = new PriceGraphBuilder("price-mapping")
            if (input.graphSource === "prod") {
                if (input.prodGraph) builder.initFromSerialized(input.prodGraph.graph)
            } else {
                if (!input.entry || !catalog) throw new Error("Code graph requires a registry entry")
                this.addPriceGraphData(builder, input.entry.createGraph(catalog))
            }

            let priceMap: PriceMap
            if (input.priceSource === "db") {
                priceMap = await getPriceMap(this.injector, await this.getOrganizationId(), builder.rootNode.getOriginalIdsFromCatalog())
            } else {
                if (!catalog) throw new Error("Catalog prices require a registry entry or a loaded catalog")
                priceMap = catalog.getPriceMap()
            }
            if (isStale()) return

            this.catalog = catalog
            this.priceGraphBuilder = builder
            this.$priceMap.set(priceMap)
            this.error = ""
            this.$dataSource().data = []
            this.changeDetectorRef.detectChanges()
            this.$dataSource().data = this.priceGraphBuilder.getNodesForDisplay()
            this.updateCurrentConfigurations()
        } catch (error) {
            if (isStale()) return
            this.logger.error("Rebuilding price graph failed:", error)
            this.error = error instanceof Error ? error.message : String(error)
            this.changeDetectorRef.markForCheck()
        }
    }

    private getEntryCatalog(entry: TemplateEntry): Promise<Catalog> {
        const {catalog} = entry
        let result = this.catalogCache.get(catalog.id)
        if (!result) {
            result = (async () => {
                const data = await lastValueFrom(this.http.get(catalog.asset, {responseType: "blob"}))
                return catalog.parse(new Uint8Array(await data.arrayBuffer()), await import("xlsx"), this.logger)
            })()
            result.catch(() => this.catalogCache.delete(catalog.id))
            this.catalogCache.set(catalog.id, result)
        }
        return result
    }

    private getOrganizationId(): Promise<string> {
        const templateId = this.$templateId()
        if (!templateId) throw new Error("No template uuid provided")
        let result = this.organizationIdCache.get(templateId)
        if (!result) {
            result = fetchThrowingErrors(this.organizationIdFromTemplateIdGql)({templateId}).then(({template}) => template.organizationId)
            result.catch(() => this.organizationIdCache.delete(templateId))
            this.organizationIdCache.set(templateId, result)
        }
        return result
    }

    private addPriceGraphData(builder: PriceGraphBuilder, data: PriceGraphData): void {
        data.amountGroups.forEach((group) => builder.addAmountGroupNode(group))
        data.priceContainers.forEach((container) => builder.addSubPriceGraph(container))
        data.pricedItemNodes.forEach((pricedItem) => builder.addSubPriceGraph(pricedItem))
    }

    async deletePriceGraph() {
        if (!this.priceGraphId) {
            throw new Error("No price graph uuid provided")
        }
        const result = await mutateThrowingErrors(this.deletePriceGraphGql)({priceGraphId: this.priceGraphId})
        this.logger.debug("Delete result", result)
        this.priceGraphId = undefined
        this.priceGraphState = PriceGraphState.Draft
        this.$prodGraph.set(undefined)
    }

    //Uses the parser of the selected entry, customer files are too inconsistent to guess the format.
    async initCatalogFromLocalFile(event: Event) {
        const file = (event.target as HTMLInputElement).files?.[0]
        const catalog = await getCatalogFromFileInput(event, this.logger, this.$selectedEntry()?.catalog.parse)
        this.$catalogOverride.set({name: file?.name ?? "unknown", catalog})
    }

    async initNewCatalogFromLocalFile(event: Event) {
        this.catalogNew = await getCatalogFromFileInput(event, this.logger)
    }

    async verifyAndSave() {
        if (!this.priceGraphBuilder.rootNode) {
            return
        }

        const templateId = this.$templateId()
        if (!templateId) {
            throw new Error("No template uuid provided")
        }

        const organizationId = await fetchThrowingErrors(this.organizationIdFromTemplateIdGql)({templateId}).then(({template}) => template.organizationId)

        try {
            //Only save, if prices can be loaded for this graph, otherwise the price computation will not work in practice.
            await getPriceMap(this.injector, organizationId, this.priceGraphBuilder.rootNode.getOriginalIdsFromCatalog())
            checkConfigurationGroupNodes(this.priceGraphBuilder.rootNode)

            /*Check, to what extend this is actually necessary:
            - the check is *very* helpful for some graph types (Darran-style)
            - Cannot be used for Vado at the moment
            - The check must be adapted to new node conditions
            - Some nodes must be excludeable*/
            //checkMutuallyExclusiveConditions(this.priceGraphBuilder.rootNode)
            return this.save(organizationId)
        } catch (error) {
            this.logger.error("An error occurred, did not save:", error)
        }
    }

    async save(organizationId: string) {
        const originalIdsFromCatalog = this.priceGraphBuilder.rootNode.getOriginalIdsFromCatalog()
        const graph = this.priceGraphBuilder.rootNode.serialize()
        if (!this.priceGraphId) {
            const templateId = this.$templateId()
            if (!templateId) {
                throw new Error("No template uuid provided")
            }
            const createPriceGraphInput: MutationCreatePriceGraphInput = {
                originalIdsFromCatalog: originalIdsFromCatalog,
                templateId,
                organizationId: organizationId,
                state: this.priceGraphState,
                graph,
            }
            const {createPriceGraph} = await mutateThrowingErrors(this.createPriceGraphGql)({input: createPriceGraphInput})
            this.priceGraphId = createPriceGraph?.id
        } else {
            const updatePriceGraphInput: MutationUpdatePriceGraphInput = {
                originalIdsFromCatalog: originalIdsFromCatalog,
                priceGraphId: this.priceGraphId,
                organizationId: organizationId,
                state: this.priceGraphState,
                graph,
            }
            const result = await mutateThrowingErrors(this.updatePriceGraphGql)({input: updatePriceGraphInput})
            this.logger.debug("Update result", result)
        }

        if (this.priceGraphId) this.$prodGraph.set({id: this.priceGraphId, state: this.priceGraphState, graph})
    }

    updateCurrentConfigurations(): void {
        this.updatePrice()
    }

    updatePrice(): void {
        const pricingContext = this.$pricingContext()
        if (!pricingContext) {
            this.logger.warn("Pricing context not initialized, cannot update prices")
            return
        }
        if (this.$selectedCurrency() === undefined) {
            this.$selectedCurrency.set(pricingContext.getCurrency())
        }
        const graphAccessor = new PriceGraphAccessor(this.priceGraphBuilder.rootNode)
        this.pricingService
            .getPricesForMappingData(graphAccessor, pricingContext)
            .then((result) => {
                this.$currentPrices.set(result)
                this.error = ""
            })
            .catch((error) => {
                this.error = error.message
                this.$currentPrices.set([])
            })
    }

    onCurrencyChange(currency: Currency): void {
        this.$selectedCurrency.set(currency)
        this.$dataSource().data = []
        this.changeDetectorRef.detectChanges()
        this.$dataSource().data = this.priceGraphBuilder.getNodesForDisplay()
        this.updatePrice()
    }

    nodeSelected(node: PricingNode): boolean {
        return this.$selectedTreeNode() === node
    }
}
